
export default function ProductionFacilities() {
  const facilities = [
    { icon: 'ri-tools-line', name: 'Assembly Area', detail: 'Control Cabinets Manufacturing' },
    { icon: 'ri-flask-line', name: 'Laboratory', detail: 'Water Analysis & Testing' },
    { icon: 'ri-archive-line', name: 'Warehouse', detail: 'Component Storage' },
  ];

  const pilotEquipment = [
    { icon: 'ri-disc-line', name: 'DTRO Pilot Unit' },
    { icon: 'ri-filter-3-line', name: 'UF Pilot Unit' },
    { icon: 'ri-dashboard-line', name: 'Control Cabinet' },
    { icon: 'ri-refresh-line', name: 'CIP Washing Unit' },
  ];

  const clientResults = [
    { icon: 'ri-database-2-line', text: 'Comprehensive Data' },
    { icon: 'ri-lightbulb-line', text: 'Optimal Solution' },
    { icon: 'ri-bar-chart-line', text: 'Comparative Analysis' },
    { icon: 'ri-shield-check-line', text: 'Risk Minimization' },
    { icon: 'ri-file-list-3-line', text: 'Full Report' },
  ];

  return (
    <section id="production" className="bg-gradient-to-br from-[#EDF4FF] via-[#F5F9FF] to-[#E8F2FF] py-16 lg:py-24 relative overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-8">

        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-[2px] bg-[#0A1628]"></div>
          <span className="text-xs uppercase tracking-[3px] text-[#0A1628]/50 font-bold">Production Facilities</span>
        </div>
        <h2 className="text-[32px] sm:text-[40px] lg:text-[52px] font-black text-[#0A1628] uppercase leading-none mb-10 lg:mb-16 tracking-tight">
          PRODUCTION &amp;<br />PILOT PARK
        </h2>

        {/* Row 1: 3 cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">

          {/* Card 1 — Dominant number, light */}
          <div className="rounded-[24px] bg-white border border-[#E8F0FF] p-7 lg:p-10 flex flex-col justify-between min-h-[240px] lg:min-h-[300px]">
            <div className="text-[48px] sm:text-[64px] lg:text-[96px] font-black text-[#0A1628] leading-none tracking-tighter">2,500+</div>
            <div>
              <div className="text-[10px] uppercase tracking-[3px] text-[#0A1628]/40 font-bold mb-2">Production Area</div>
              <div className="text-base font-black text-[#0A1628] uppercase tracking-wide mb-2">m² Total Facility</div>
              <div className="text-sm text-[#4A5568] leading-relaxed">
                Full-cycle production site: assembly, laboratory, warehouse and specialized pilot park.
              </div>
            </div>
          </div>

          {/* Card 2 — Photo with overlay */}
          <div className="rounded-[24px] overflow-hidden relative min-h-[240px] lg:min-h-[300px]">
            <img
              src="https://public.readdy.ai/ai/img_res/69eb6032bfb1471df208501237ba8013.jpg"
              alt="Production Facility"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-[#0A1628]/60"></div>
            <div className="relative z-10 p-7 lg:p-10 flex flex-col justify-between h-full">
              <div className="text-[10px] uppercase tracking-[3px] text-white/50 font-bold">Facility Overview</div>
              <div>
                <div className="text-base font-black text-white uppercase tracking-wide mb-4">Assembly &amp; Production</div>
                <ul className="space-y-3">
                  {facilities.map((f, i) => (
                    <li key={i} className="flex items-center gap-3 text-sm text-white/80">
                      <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                        <i className={`${f.icon} text-[14px] text-white`}></i>
                      </div>
                      <span><strong className="text-white">{f.name}</strong> — {f.detail}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* Card 3 — Dark, Pilot Park */}
          <div className="rounded-[24px] bg-[#0A1628] p-7 lg:p-10 flex flex-col justify-between min-h-[240px] lg:min-h-[300px] sm:col-span-2 lg:col-span-1">
            <div>
              <div className="text-[10px] uppercase tracking-[3px] text-white/40 font-bold mb-3">Specialized</div>
              <div className="text-[36px] sm:text-[44px] lg:text-[52px] font-black text-white leading-none tracking-tighter">PILOT</div>
              <div className="text-[36px] sm:text-[44px] lg:text-[52px] font-black text-white leading-none opacity-30 tracking-tighter">PARK</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[3px] text-white/40 font-bold mb-4">Equipment</div>
              <div className="grid grid-cols-2 gap-2">
                {pilotEquipment.map((eq, i) => (
                  <div key={i} className="flex items-center gap-2 bg-white/8 border border-white/10 rounded-[10px] px-3 py-2">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className={`${eq.icon} text-[13px] text-white/70`}></i>
                    </div>
                    <span className="text-xs font-semibold text-white/80 leading-tight">{eq.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: Client Results full-width card */}
        <div className="rounded-[24px] bg-[#3B9ED6] p-7 lg:p-10">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
            <div className="flex-shrink-0">
              <div className="text-[10px] uppercase tracking-[3px] text-white/50 font-bold mb-2">Client Results</div>
              <div className="text-lg lg:text-xl font-black text-white uppercase tracking-tight">
                What You Get After Pilot Testing
              </div>
            </div>
            <div className="flex flex-wrap gap-3">
              {clientResults.map((result, i) => (
                <div
                  key={i}
                  className="flex items-center gap-3 bg-white/15 border border-white/20 rounded-[12px] px-4 py-2.5 lg:px-5 lg:py-3 cursor-pointer hover:bg-white hover:border-white group transition-all duration-200"
                >
                  <div className="w-5 h-5 flex items-center justify-center">
                    <i className={`${result.icon} text-[15px] text-white group-hover:text-[#3B9ED6] transition-colors`}></i>
                  </div>
                  <span className="text-sm font-bold text-white group-hover:text-[#3B9ED6] transition-colors whitespace-nowrap">{result.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </section>
  );
}
